import { useMemo } from 'react';
import { fmtMg, ymdAddDays } from '../../lib/format';
import type { TaperDayWire } from '@shared/types';

interface LastWeekDoseListProps {
  /** Today's local YYYY-MM-DD — the most recent row in the list. */
  today: string;
  /** Daily totals already bucketed in the user's local TZ. */
  actualByDate: Record<string, number>;
  /** Plan days, used for the per-day target column. */
  planDays: TaperDayWire[];
  /** How many trailing days to show. Defaults to 7. */
  days?: number;
}

export interface LastWeekRow {
  ymd: string;
  /** "Today", "Yesterday", or short weekday like "Wed". */
  dayLabel: string;
  /** Numeric date like "5/21". */
  dateLabel: string;
  actual: number | null;
  target: number | null;
  isHold: boolean;
  status: 'on' | 'over' | 'under' | 'none' | 'hold';
  deltaPct: number | null;
}

type Row = LastWeekRow;

/**
 * Last 7 days of dose totals as a tabular list, newest-first.
 *
 * Pairs with the line chart but answers a different question: "how
 * have my last several days actually gone, day by day?" The chart
 * shows trend at a glance; the list shows magnitudes you can read.
 *
 * Status semantics mirror the chart's adherence band:
 *   - on:   within ±10% of target (the chart's filled band)
 *   - over: > +10% of target (warning tone)
 *   - under: < -10% of target (success tone — taper is working)
 *   - hold: a `isHold` day, target unchanged from yesterday
 *   - none: no doses logged that day
 */
export function LastWeekDoseList({
  today,
  actualByDate,
  planDays,
  days = 7,
}: LastWeekDoseListProps) {
  const rows = useMemo<Row[]>(() => buildLastWeekRows(today, actualByDate, planDays, days), [
    today,
    actualByDate,
    planDays,
    days,
  ]);

  const totalActual = useMemo(() => {
    return rows.reduce((sum, r) => sum + (r.actual ?? 0), 0);
  }, [rows]);
  const totalTarget = useMemo(() => {
    return rows.reduce((sum, r) => sum + (r.target ?? 0), 0);
  }, [rows]);
  const totalDeltaPct = totalTarget > 0
    ? Math.round(((totalActual - totalTarget) / totalTarget) * 100)
    : null;

  return (
    <div className="last-week-list" role="table" aria-label={`Last ${days} days of doses`}>
      <div className="last-week-list-rows">
        {rows.map((r) => (
          <DayRow key={r.ymd} row={r} />
        ))}
      </div>
      <div className="last-week-list-footer" role="row">
        <span className="last-week-list-footer-label">
          {days}-day total
        </span>
        <span className="last-week-list-footer-numbers mono">
          {fmtMg(totalActual)} of {fmtMg(totalTarget)}
        </span>
        {totalDeltaPct != null ? (
          <span
            className="last-week-list-footer-delta"
            data-tone={totalDeltaPct > 10 ? 'over' : totalDeltaPct < -10 ? 'under' : 'on'}
          >
            {totalDeltaPct > 0 ? '+' : ''}{totalDeltaPct}% vs plan
          </span>
        ) : null}
      </div>
    </div>
  );
}

function DayRow({ row }: { row: Row }) {
  const { dayLabel, dateLabel, actual, target, status, deltaPct } = row;

  // Bar width = actual / (target * 1.1) — capped at 100% of the bar
  // width when over. The fill color encodes status so a glance reads
  // "in band / over / under / nothing logged."
  const denom = target != null && target > 0 ? target * 1.1 : 0;
  const fillFrac = denom > 0 && actual != null ? Math.min(1, actual / denom) : 0;

  const statusLabel = (() => {
    if (status === 'hold') return 'hold day';
    if (status === 'none') return 'no doses';
    if (status === 'on') return 'on target';
    if (status === 'over') return `over ${deltaPct! > 0 ? '+' : ''}${deltaPct}%`;
    return `under ${deltaPct}%`;
  })();

  return (
    <div className="last-week-row" role="row" data-status={status}>
      <div className="last-week-row-day" role="cell">
        <span className="last-week-row-day-name">{dayLabel}</span>
        <span className="last-week-row-day-date">{dateLabel}</span>
      </div>
      <div className="last-week-row-numbers mono" role="cell">
        {actual != null ? fmtMg(actual) : '—'}
        <span className="dim"> / {target != null ? fmtMg(target) : '—'}</span>
      </div>
      <div
        className="last-week-row-bar"
        role="cell"
        aria-hidden="true"
      >
        <div
          className="last-week-row-bar-fill"
          style={{ inlineSize: `${Math.round(fillFrac * 100)}%` }}
          data-status={status}
        />
      </div>
      <div className="last-week-row-status" role="cell" data-status={status}>
        {statusLabel}
      </div>
    </div>
  );
}

// Build the rows for the list. Walks today → today-(days-1) so the
// newest day is on top. Reads actualByDate (already bucketed by the
// caller in the user's local TZ) and planDays for the target column.
// Exported for unit testing.
export function buildLastWeekRows(
  today: string,
  actualByDate: Record<string, number>,
  planDays: TaperDayWire[],
  days: number,
): Row[] {
  // Plan lookup. Pre-built so we don't .find() inside the loop.
  const planByDate: Record<string, TaperDayWire> = {};
  for (const d of planDays) planByDate[d.date] = d;

  const out: Row[] = [];
  for (let offset = 0; offset < days; offset += 1) {
    const ymd = ymdAddDays(today, -offset);
    const planDay = planByDate[ymd];
    const target = planDay?.targetMg ?? null;
    const isHold = planDay?.isHold ?? false;
    const rawActual = actualByDate[ymd];
    const actual = typeof rawActual === 'number' ? rawActual : null;

    let status: Row['status'];
    let deltaPct: number | null = null;
    if (actual == null || actual === 0) {
      status = isHold ? 'hold' : 'none';
    } else if (target == null || target <= 0) {
      // No target on file (off-plan day) — show actual, neutral tone.
      status = 'none';
    } else {
      const diff = actual - target;
      const pct = Math.round((diff / target) * 100);
      deltaPct = pct;
      if (pct > 10) status = 'over';
      else if (pct < -10) status = 'under';
      else status = 'on';
    }

    out.push({
      ymd,
      dayLabel: dayLabel(ymd, offset),
      dateLabel: dateLabel(ymd),
      actual,
      target,
      isHold,
      status,
      deltaPct,
    });
  }
  return out;
}

// "Today", "Yesterday", or short weekday for older days.
function dayLabel(ymd: string, offset: number): string {
  if (offset === 0) return 'Today';
  if (offset === 1) return 'Yesterday';
  try {
    const d = new Date(`${ymd}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  } catch {
    return '';
  }
}

// Month/day label like "5/21". Built by hand so format is stable
// across locales.
function dateLabel(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
